import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, catchRenderException, Endpoint, IeAnexo, onCatchError, setPredefinedFiles, setRequiredField, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { BaseFileUploaderAPI, BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues, ChecklistAPI, ChecklistItem, FetchAPI, FieldAPI, FormAPI, MapperAPI, PanelAPI, WizardAPI } from '@philips-emr/tws-components';
import { ComponentAPI } from '@philips-emr/tws-core';
import Controller, { OnAfterNavigate, OnBlur, OnChange, OnMount, OnSelect, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, MountEvent, NavigateEvent, SelectEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HistoryService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod, RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject, isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { ReinclusaoBeneficiarioAliases as Aliases, FragmentNames } from '../../codegen';
import { Anexo } from '../../interfaces/anexo';
import { EnderecoData } from '../../interfaces/logradouro';
import BaseController from '../BaseController';

interface Dependente {
  nrSeqSegurado: number;
  nmSegurado: string;
  ieCheck?: number;
}

interface Cidade {
  cdMunicipioIbge: string;
  dsMunicipio: string;
}

const identifierStepTitular = 'wizard-step-titular';
const identifierStepAnexo = 'wizard-step-anexo';
const identifierStepDependente = 'wizard-step-dependente';

const identifiersSteps = [
  identifierStepTitular,
  identifierStepAnexo,
  identifierStepDependente,
];

@Controller({ fragment: FragmentNames.REINCLUSAO_BENEFICIARIO })
export default class ReinclusaoBeneficiarioController extends BaseController {

  private regraAnexos: Anexo[];

  @Inject(Aliases.WIZARD_REINCLUSAO)
  private wizardReinclusao: WizardAPI;

  @Inject(Aliases.FETCH_CIDADE)
  private fetchCidade: FetchAPI;

  @Inject(Aliases.FORM_TITULAR)
  private formTitular: FormAPI;

  @Inject(Aliases.FORM_ANEXO)
  private formAnexo: FormAPI;

  @Inject(Aliases.CHECKLIST_DEPENDENTE)
  private checklistDependente: ChecklistAPI;

  @Inject(Aliases.MAPPER_ANEXO)
  private mapperAnexo: MapperAPI;

  @Inject(Aliases.PANEL_ANEXO)
  private panelAnexo: PanelAPI;

  @Inject(Aliases.FIELD_ANEXO)
  private fieldAnexo: FieldAPI;

  @Inject(Aliases.FIELD_CIDADE)
  private fieldCidade: FieldAPI;

  private mapStepForm: Map<string, FormAPI> = new Map();
  private cidade: Cidade | undefined;

  private readonly ieFuncionalidade = 17;

  @OnMount(Aliases.PANEL_REINCLUSAO_BENEF)
  public onInitProps(): void {
    this.mapStepForm = new Map();
  }

  @OnValidate(Aliases.FORM_TITULAR)
  public onValidateFormTitular(event: ValidateEvent): void {
    this.setStepDone(identifierStepTitular, event);
  }

  @OnValidate(Aliases.FORM_DEPENDENTE)
  public onValidateFormDependente(event: ValidateEvent): void {
    this.setStepDone(identifierStepDependente, event);
  }

  @OnValidate(Aliases.FORM_ANEXO)
  public onValidateFormAnexo(event: ValidateEvent): void {
    this.setStepDone(identifierStepAnexo, event);
  }

  @OnAfterNavigate(Aliases.WIZARD_REINCLUSAO)
  public onAfterNavigate(event: NavigateEvent<string>): void {
    const { to, from } = event;

    if (!to) {
      return;
    }

    const formTo = this.mapStepForm.get(to);
    formTo?.setEditing(true);
    formTo?.validate();

    const formFrom = this.mapStepForm.get(from);
    formFrom?.submit();
  }

  @OnMount([Aliases.FORM_TITULAR, Aliases.FORM_DEPENDENTE, Aliases.FORM_ANEXO])
  public onMountMapStepForm(event: MountEvent): void {
    const api = event.target as unknown as FormAPI;
    const alias = api.state.alias as string ?? '';
    const formName = alias.replace('form-', '');

    if (isEmptyOrBlankString(formName)) {
      return;
    }

    const identificador = identifiersSteps.find((id) => id.includes(formName));
    identificador && this.mapStepForm.set(identificador, api);
  }

  @OnMount(Aliases.FILE_UPLOADER_ANEXO)
  public onMountFileUploaderAnexo(event: MountEvent): void {
    const api = event.target as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;

    void setPredefinedFiles(this.ieFuncionalidade, api, []).then((anexos) => {
      if (isEmptyArrayOrObject(anexos)) {
        return;
      }

      this.context.set({ regraAnexos: anexos });

      const isRequired = anexos.some((anexo) => anexo.ieObrigatorio === 'S');

      if (isRequired) {
        setRequiredField(true, this.fieldAnexo);
        this.fieldAnexo.validate();
      }

      this.formAnexo.validate();
    });
  }

  @OnMount(Aliases.WIZARD_REINCLUSAO)
  public onMountInitForms(): void {
    const { nrSeqContrato, nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();

    if (!nrSeqBeneficiario) {
      return;
    }

    void this.fetchShowRenderStepAnexo().then((showStepAnexo) => {
      void this.fetchGetReinclusao(nrSeqContrato, nrSeqBeneficiario).then((response) => {
        const { nrSeqSegurado, nrSeqReinclusao, sgEstado, dsMunicipio, cdMunicipioIbge } = response;

        this.cidade = { dsMunicipio, cdMunicipioIbge };
        this.formTitular.setData(response);

        if (sgEstado) {
          catchRenderException(() => this.fetchCidade.setParameters({ dsUnidadeFederacao: sgEstado }));
        }

        void this.fetchDependentes(nrSeqContrato, nrSeqSegurado, nrSeqReinclusao).then((showStepDependente) => {
          this.wizardReinclusao.setHiddenSteps([
            ...(!showStepDependente ? [identifierStepDependente] : []),
            ...(!showStepAnexo ? [identifierStepAnexo] : []),
          ]);

          this.formTitular.validate();

          if (showStepAnexo) {
            void this.fetchAnexos(nrSeqContrato, nrSeqReinclusao);
          }
        });
      });
    });
  }

  @OnSubmit(Aliases.WIZARD_REINCLUSAO)
  public save(): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    const body: any = {
      ...Array.from(this.mapStepForm.values())
        .map((form) => form.getRecord())
        .reduce((accumulator, value) => accumulator = { ...accumulator, ...value }),
      ...this.cidade,
    };

    if (body.dependentes) {
      body.dependentes = (body.dependentes as ChecklistItem[])
        .map<Dependente>((item) => ({ nrSeqSegurado: Number(item.key), nmSegurado: item.label }));
    }

    body.anexos = this.handleAnexos(body.anexos as Anexo[]);

    HttpService.post(Endpoint.POST.REINCLUSAO_BENEFICIARIO, { nrSeqContrato }, { body })
      .then(() => HistoryService.push(APPLICATION_ROUTES.REINCLUSAO_BENEFICIARIO, { nrSeqContrato }))
      .catch(() => onCatchError());
  }

  @OnChange(Aliases.UNBOXED_AUTOCOMPLETE_CIDADE)
  public onChangeUnboxedAutocompleteCidade(e: ChangeEvent<string>): void {
    const api = e.target as unknown as ComponentAPI<any>;
    const cidades = (api.state.data as unknown as Cidade[]) ?? [];
    const dsMunicipio = cidades.find((cidade) => cidade.cdMunicipioIbge === e.value)?.dsMunicipio || '';

    this.cidade = { dsMunicipio, cdMunicipioIbge: e.value };
  }

  @OnChange(Aliases.FIELD_ESTADO)
  public onChangeSgEstado(e: ChangeEvent<string>): void {
    this.fieldCidade.setValue(null);
    this.fetchCidade.setParameters({ dsUnidadeFederacao: e.value ? e.value : null });
    this.fetchCidade.forceFetch();
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  public onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { value } = event;
    const { filename, nrSeqAnexo } = value;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.REINCLUSAO_BENEF_ANEXO;

    FileService.download(Endpoint.GET.REINCLUSAO_BENEFICIARIO_ANEXO_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnBlur(Aliases.FIELD_CEP)
  public onBlurCdCep(event: FocusEvent): void {
    const api = event.target as unknown as FieldAPI;

    if (api.isValid() && api.getValue()) {
      void HttpService.get<EnderecoData>(Endpoint.GET.ENDERECO_CEP, { cdCep: api.getValue() })
        .then((response) => this.setEndereco(response));
    }
  }

  private setEndereco(response: RequestResponse<EnderecoData>): void {
    if (response.body) {
      const { dsUf } = response.body;

      this.formTitular.setData({
        ...this.formTitular.getRecord(),
        ...response.body,
        sgEstado: dsUf,
      });

      if (dsUf) {
        this.context.set({ dsUnidadeFederacaoSgEstado: dsUf });
        this.fetchCidade.forceFetch();
      }
    }
  }

  private fetchShowRenderStepAnexo(): Promise<boolean> {
    return HttpService.get<number>(Endpoint.GET.CHECK_QTD_REGRA_ANEXOS, { ieFuncionalidade: this.ieFuncionalidade })
      .then(({ body }) => !!(body && body > 0))
      .catch(() => false);
  }

  private fetchGetReinclusao(nrSeqContrato: number | string, nrSeqBeneficiario: number | string): Promise<any> {
    return HttpService.get(Endpoint.GET.REINCLUSAO_BENEFICIARIO, { nrSeqContrato, nrSeqBeneficiario }).then(({ body: response }) => response);
  }

  private fetchDependentes(nrSeqContrato: number | string, nrSeqBeneficiario: number, nrSeqReinclusao: number): Promise<boolean> {
    return HttpService.get<Dependente[]>(Endpoint.GET.REINCLUSAO_BENEFICIARIO_DEPENDENTE, { nrSeqContrato, nrSeqBeneficiario, ...nrSeqReinclusao && { nrSeqReinclusao } }).then(({ body: dependentes }) => {
      const itens = dependentes?.map((dependente) => ({ key: String(dependente.nrSeqSegurado), label: dependente.nmSegurado, value: String(dependente.nrSeqSegurado) })) ?? [];

      const values = dependentes?.filter((dependente) => dependente.ieCheck == 1)
        .map<ChecklistItem>((dependente) => ({ key: String(dependente.nrSeqSegurado), label: dependente.nmSegurado, value: dependente.ieCheck == 1 })) ?? [];

      this.checklistDependente.setData(itens as any);
      this.checklistDependente.setValue(values);
      return !!dependentes?.length;
    });
  }

  private fetchAnexos(nrSeqContrato: number | string, nrSeqBeneficiario: number): void {
    if (nrSeqContrato && nrSeqBeneficiario) {
      void HttpService.get<Anexo[]>(Endpoint.GET.REINCLUSAO_BENEFICIARIO_ANEXOS, { nrSeqContrato, nrSeqBeneficiario })
        .then(({ body: anexos }) => {
          if (anexos && anexos.length) {
            this.panelAnexo.setVisible(true);
            this.mapperAnexo.setData(anexos as any);

            this.formAnexo.setData({ ...this.formAnexo.getRecord(), anexos });
            this.fieldAnexo.validate();
          }
        });
    }
  }

  private setStepDone(identifier: string, event: ValidateEvent): void {
    const form = event.target as FormAPI;

    if (this.wizardReinclusao.getCurrentStepIdentifier() === identifier) {
      this.wizardReinclusao.setStepDone(identifier, form.isValid());
    }
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

}
